import { toast } from 'sonner';

interface ToastMessages<T> {
  loading: string;
  success: string | ((data: T) => string);
  error?: string | ((err: Error) => string);
}

/**
 * Wraps an API call with a Sonner toast.promise and properly unwraps the result.
 *
 * This centralizes the toast.promise().unwrap() pattern to:
 * - Prevent the bug where omitting .unwrap() causes TanStack Query to receive a
 *   ToastPromise wrapper instead of the actual API response
 * - Provide a consistent default error handler (shows err.message)
 * - Reduce mutation boilerplate from ~8 lines to 1
 *
 * @example
 * useMutation({
 *   mutationFn: (id: string) =>
 *     toastMutate(HostsAPI.remove(id), {
 *       loading: 'Deleting host...',
 *       success: 'Host deleted successfully',
 *     }),
 * })
 */
export async function toastMutate<T>(promise: Promise<T>, messages: ToastMessages<T>): Promise<T> {
  return toast
    .promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: (err) => {
        if (err instanceof Error) {
          return typeof messages.error === 'function'
            ? messages.error(err)
            : (messages.error ?? err.message);
        }
        return typeof messages.error === 'string' && messages.error !== ''
          ? messages.error
          : 'An unexpected error occurred';
      },
    })
    .unwrap();
}
